export interface WorkIdRecord {
  id: number;
  workIdentificationId: string | null;
  status: string | null;
  comments: string | null;
  reportedOn: string;
  foremanAreaId: number | null;
  createdFromFormId: string | null;
  formName?: string | null;
  createdAt: string;
  updatedAt: string;
  priority?: string | null;
  reportedBy?: { name: string | null; lastName: string | null } | null;
  foremanArea?: { name: string } | null;
  createdFromForm?: { id: string; name: string } | null;
  asset?: { id: number; name: string; highwayUnique?: string | null; assetNumber?: string | null } | null;
  /** True when this Work ID has at least one merge suggestion. */
  hasMergeSuggestions?: boolean;
  /** Distinct reason tags across all merge suggestions (e.g. "similar comments", "close GPS"). */
  mergeReasonTags?: string[];
  /** Linked task id; a Work ID has at most one task. */
  taskId?: number | null;
  taskLinkedAt?: string | null;
  taskLinkedById?: number | null;
  /** True when this Work ID was closed by closing its linked task. */
  closedWithTask?: boolean;
  /** Linked task for list display, or null. The map endpoint omits completionDate. */
  task?: { id: number; taskNumber: string | null; type: string; status: string | null; completionDate?: string | null } | null;
  /** GPS coordinates for map display. */
  latitude?: number | null;
  longitude?: number | null;
  /** Activity when available (e.g. from form or detail). */
  activity?: { id?: number; activityNumber?: string | null; activityName?: string | null } | null;
  /** Present when this Work ID was merged into a parent (child row). */
  mergedIntoParent?: { id: number; workIdentificationId: string | null };
}

/** Date filter mode: by Work ID reported date or by linked task completion date */
export type WorkIdDateFilterType = 'reportedOn' | 'taskCompletion';

/**
 * Query params for the Work ID list/map endpoints.
 *
 * Filter params accept a single bare value ("3") or a comma-separated list ("3,4");
 * an empty string means "no filter".
 */
export interface WorkIdQueryParams {
  page?: number;
  limit?: number;
  /** Comma-separated statuses, or the sentinels "all" / "exclude-cancelled" (default view). */
  status?: string;
  /** Comma-separated foreman area ids. */
  foremanAreaId?: string;
  search?: string;
  /** Comma-separated service area ids. */
  serviceAreaId?: string;
  /** Comma-separated asset ids (no longer sent by the list UI; kept for other callers). */
  assetId?: string;
  /** Comma-separated Asset.assetType values (e.g. "road,structure"). */
  assetType?: string;
  assetSearch?: string;
  /** Comma-separated priorities (e.g. "Medium,High") */
  priority?: string;
  /** Comma-separated reporter user ids. */
  reportedById?: string;
  createdFromFormId?: string;
  sourceFormName?: string;
  /** Comma-separated work-source tokens: `form|<formId>` and/or `name|<url-encoded name>`. */
  workSource?: string;
  /** Start of date range (YYYY-MM-DD) */
  dateFrom?: string;
  /** End of date range (YYYY-MM-DD) */
  dateTo?: string;
  /** Whether to filter by reported date or task completion date */
  dateFilterType?: WorkIdDateFilterType;
  /** Server-side sort column (allowlisted server-side): workIdentificationId, reportedOn, priority, status, reportedBy, asset, foremanArea, source. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export type WorkIdSourceFilterOption =
  | { kind: 'form'; formId: string; label: string }
  | { kind: 'formName'; formName: string; label: string };

export interface WorkIdFilterAssetOption {
  id: number;
  name: string;
  assetNumber: string | null;
  assetType: string | null;
}

export interface WorkIdFilterOptions {
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{ id: number; name: string; serviceAreaId: number; serviceAreaName?: string }>;
  reporters: Array<{ id: number; name: string | null; lastName: string | null }>;
  sources: WorkIdSourceFilterOption[];
  assets?: WorkIdFilterAssetOption[];
}

/**
 * Full filter state for Work ID list (used by WorkIdsPage, WorkIdsTable, WorkIdFilters).
 *
 * Every multiselect filter is stored as a comma-separated string so it can be passed to the
 * API and read from the URL unchanged. `dateFilterType` is a mode, not a filter value.
 */
export interface WorkIdListFilters {
  serviceAreaId: string;
  foremanAreaId: string;
  /** Comma-separated Asset.assetType values ("road" / "structure"). Replaced the per-asset filter. */
  assetType: string;
  status: string;
  search?: string;
  priority?: string;
  dateFrom?: string;
  dateTo?: string;
  dateFilterType?: WorkIdDateFilterType;
  reportedById: string;
  workSource: string;
}

export interface PaginatedWorkIdsResponse {
  data: WorkIdRecord[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const workIdService = {
  async getWorkIdsForMap(params: Omit<WorkIdQueryParams, 'page' | 'limit'> = {}, signal?: AbortSignal): Promise<{ data: WorkIdRecord[] }> {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.foremanAreaId) queryParams.append('foremanAreaId', params.foremanAreaId);
    if (params.search) queryParams.append('search', params.search);
    if (params.serviceAreaId) queryParams.append('serviceAreaId', params.serviceAreaId);
    if (params.assetId) queryParams.append('assetId', params.assetId);
    if (params.assetType) queryParams.append('assetType', params.assetType);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.reportedById) queryParams.append('reportedById', params.reportedById);
    if (params.createdFromFormId) queryParams.append('createdFromFormId', params.createdFromFormId);
    if (params.sourceFormName) queryParams.append('sourceFormName', params.sourceFormName);
    if (params.workSource) queryParams.append('workSource', params.workSource);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.dateFilterType) queryParams.append('dateFilterType', params.dateFilterType);

    const url = `${BASE_URL}/work-ids/map?${queryParams.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      ...(signal && { signal })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch work IDs for map: ${response.statusText}`);
    }

    return response.json();
  },

  async getWorkIds(params: WorkIdQueryParams = {}, signal?: AbortSignal): Promise<PaginatedWorkIdsResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.foremanAreaId) queryParams.append('foremanAreaId', params.foremanAreaId);
    if (params.search) queryParams.append('search', params.search);
    if (params.serviceAreaId) queryParams.append('serviceAreaId', params.serviceAreaId);
    if (params.assetId) queryParams.append('assetId', params.assetId);
    if (params.assetType) queryParams.append('assetType', params.assetType);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.reportedById) queryParams.append('reportedById', params.reportedById);
    if (params.createdFromFormId) queryParams.append('createdFromFormId', params.createdFromFormId);
    if (params.sourceFormName) queryParams.append('sourceFormName', params.sourceFormName);
    if (params.workSource) queryParams.append('workSource', params.workSource);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.dateFilterType) queryParams.append('dateFilterType', params.dateFilterType);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${BASE_URL}/work-ids?${queryParams.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      ...(signal && { signal })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch work IDs: ${response.statusText}`);
    }

    return response.json();
  },

  async getFilterOptions(serviceAreaId?: string): Promise<WorkIdFilterOptions> {
    const query = serviceAreaId ? `?serviceAreaId=${encodeURIComponent(serviceAreaId)}` : '';
    const url = `${BASE_URL}/work-ids/filter-options${query}`;
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch filter options: ${response.statusText}`);
    }

    return response.json();
  },

  async exportToPdf(workIdId: number): Promise<Blob> {
    const response = await fetch(`${BASE_URL}/pdf/generate-work-id-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ workIdId })
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg = (errBody as { error?: string })?.error || response.statusText;
      throw new Error(`Failed to export PDF: ${msg}`);
    }

    return response.blob();
  }
};
